/**
 * Type definitions for Produkt (Product Fit) data
 */

/**
 * Matrix item for Zielgruppe or Markt fit
 */
export interface ProduktFitMatrixItem {
  Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name?: string;
  Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent?: number;
  Produkt_Fit_Markt_Matrix_Merkmal_Name?: string;
  Produkt_Fit_Markt_Matrix_Merkmal_Prozent?: number;
}

/**
 * Product characteristic item
 */
export interface ProduktFitProduktmerkmal {
  Produkt_Fit_Produktmerkmale_Merkmal_Name: string;
  Produkt_Fit_Produktmerkmale_Merkmal_Prozent: number;
}

/**
 * Optimization suggestion for Zielgruppe
 */
export interface ProduktFitZielgruppeOptimierung {
  Name: string;
  Beschreibung: string;
  Produkt_Fit_Zielgruppe_Matrix: ProduktFitMatrixItem[];
}

/**
 * Optimization suggestion for Markt
 */
export interface ProduktFitMarktOptimierung {
  Name: string;
  Beschreibung: string;
  Produkt_Fit_Markt_Matrix: ProduktFitMatrixItem[];
}

/**
 * Strength or Weakness item
 */
export interface ProduktFitStaerkeSchwaeche {
  Name: string;
  Beschreibung: string;
}

/**
 * Main Produkt data structure from API
 */
export interface ProduktData {
  // Product info
  Produkt_Fit_Produktname: string;
  Produkt_Fit_Produktlink?: string;

  // Product characteristics
  Produkt_Fit_Produktmerkmale: ProduktFitProduktmerkmal[];

  // Zielgruppe (Target Group) fit matrix
  Produkt_Fit_Zielgruppe_Matrix: ProduktFitMatrixItem[];

  // Fit indicator (1-5 scale)
  Produkt_Fit_Indikator: number;

  // Markt (Market) fit matrix
  Produkt_Fit_Markt_Matrix: ProduktFitMatrixItem[];

  // Optimization suggestions
  Produkt_Fit_Zielgruppe_Optimierung: ProduktFitZielgruppeOptimierung[];
  Produkt_Fit_Markt_Optimierung: ProduktFitMarktOptimierung[];

  // Strengths and weaknesses
  Produkt_Fit_Staerken: ProduktFitStaerkeSchwaeche[];
  Produkt_Fit_Schwaechen: ProduktFitStaerkeSchwaeche[];

  // Sales focus
  Produkt_Fit_Vertriebsfokus: string;

  // Summary
  Produkt_Zusammenfassung: string;
}

/**
 * Combined data structure for card and overlay
 */
export interface ProduktFullData {
  main: ProduktData;
}

/**
 * Get fit indicator label based on value
 */
export function getFitIndicatorLabel(value: number): string {
  if (value >= 5) return 'Sehr Hoch';
  if (value >= 4) return 'Hoch';
  if (value >= 3) return 'Mittel';
  if (value >= 2) return 'Niedrig';
  return 'Sehr Niedrig';
}

/**
 * Get fit indicator color based on value
 */
export function getFitIndicatorColor(value: number): string {
  if (value >= 4) return '#22c55e'; // green-500
  if (value >= 3) return '#eab308'; // yellow-500
  if (value >= 2) return '#f97316'; // orange-500
  return '#ef4444'; // red-500
}

/**
 * Parse raw JSON string from API into structured Produkt data
 */
export function parseProduktData(jsonString: string): ProduktFullData | null {
  try {
    let data = jsonString;

    // If the string is wrapped in quotes, try to parse it first
    if (typeof data === 'string' && (data.startsWith('"') || data.startsWith("'"))) {
      try {
        data = JSON.parse(data);
      } catch {
        // If it fails, continue with original string
      }
    }

    const parsed = typeof data === 'string' ? JSON.parse(data) : data;

    // Handle case where it's an array - merge all elements into one object
    // The backend may return each prompt response as a separate array element
    let mainData: Record<string, unknown> | null = null;
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return null;
      mainData = {};
      for (const item of parsed) {
        if (item && typeof item === 'object') {
          Object.assign(mainData, item);
        }
      }
    } else {
      mainData = parsed;
    }

    if (!mainData) {
      return null;
    }

    return {
      main: mainData as unknown as ProduktData,
    };
  } catch {
    return null;
  }
}

/**
 * Generate mock data for development
 */
export function getMockProduktData(): ProduktFullData {
  const mockData: ProduktData = {
    Produkt_Fit_Produktname: 'RheinEnergie Ökostrom',
    Produkt_Fit_Produktlink: 'https://www.rheinenergie.com/privatkunden/strom/oekostrom/',
    Produkt_Fit_Produktmerkmale: [
      { Produkt_Fit_Produktmerkmale_Merkmal_Name: 'Nachhaltig', Produkt_Fit_Produktmerkmale_Merkmal_Prozent: 100 },
      { Produkt_Fit_Produktmerkmale_Merkmal_Name: 'Transparent', Produkt_Fit_Produktmerkmale_Merkmal_Prozent: 100 },
      { Produkt_Fit_Produktmerkmale_Merkmal_Name: 'Regional', Produkt_Fit_Produktmerkmale_Merkmal_Prozent: 100 },
      { Produkt_Fit_Produktmerkmale_Merkmal_Name: 'Sicher / verlässlich', Produkt_Fit_Produktmerkmale_Merkmal_Prozent: 100 },
      { Produkt_Fit_Produktmerkmale_Merkmal_Name: 'Fair', Produkt_Fit_Produktmerkmale_Merkmal_Prozent: 100 },
    ],
    Produkt_Fit_Zielgruppe_Matrix: [
      { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Nachhaltig', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 100 },
      { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Innovativ', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 90 },
      { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Regional', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 80 },
      { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Transparent', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 75 },
      { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Flexibel', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 70 },
    ],
    Produkt_Fit_Indikator: 5,
    Produkt_Fit_Markt_Matrix: [
      { Produkt_Fit_Markt_Matrix_Merkmal_Name: 'Nachhaltig', Produkt_Fit_Markt_Matrix_Merkmal_Prozent: 100 },
      { Produkt_Fit_Markt_Matrix_Merkmal_Name: 'Regional', Produkt_Fit_Markt_Matrix_Merkmal_Prozent: 85 },
      { Produkt_Fit_Markt_Matrix_Merkmal_Name: 'Sicher / verlässlich', Produkt_Fit_Markt_Matrix_Merkmal_Prozent: 80 },
      { Produkt_Fit_Markt_Matrix_Merkmal_Name: 'Transparent', Produkt_Fit_Markt_Matrix_Merkmal_Prozent: 75 },
      { Produkt_Fit_Markt_Matrix_Merkmal_Name: 'Innovativ', Produkt_Fit_Markt_Matrix_Merkmal_Prozent: 70 },
    ],
    Produkt_Fit_Zielgruppe_Optimierung: [
      {
        Name: 'Innovationsförderung',
        Beschreibung: 'Einführung innovativer Funktionen oder Dienstleistungen, die auf die Bedürfnisse junger Erwachsener zugeschnitten sind, wie z.B. smarte Energie-Management-Tools.',
        Produkt_Fit_Zielgruppe_Matrix: [
          { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Innovativ', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 100 },
          { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Flexibel', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 90 },
          { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Transparent', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 100 },
        ],
      },
      {
        Name: 'Flexible Tarifmodelle',
        Beschreibung: 'Entwicklung von flexiblen Preis- und Tarifmodellen, die sich an den verschiedenen Lebensstilen und finanziellen Möglichkeiten der Zielgruppe orientieren.',
        Produkt_Fit_Zielgruppe_Matrix: [
          { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Flexibel', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 90 },
        ],
      },
      {
        Name: 'Transparente Kommunikation',
        Beschreibung: 'Verbesserung der Kommunikation über die Herkunft und die Vorteile von Ökostrom, um das Vertrauen und das Verständnis bei der Zielgruppe zu stärken.',
        Produkt_Fit_Zielgruppe_Matrix: [
          { Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name: 'Transparent', Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent: 100 },
        ],
      },
    ],
    Produkt_Fit_Markt_Optimierung: [
      {
        Name: 'Regional Partnerships',
        Beschreibung: 'Aufbau von Partnerschaften mit lokalen Unternehmen und Initiativen, um die regionale Verbundenheit zu stärken und die Ansprechpartner vor Ort zu fördern.',
        Produkt_Fit_Markt_Matrix: [
          { Produkt_Fit_Markt_Matrix_Merkmal_Name: 'Regional', Produkt_Fit_Markt_Matrix_Merkmal_Prozent: 100 },
        ],
      },
      {
        Name: 'Verlässlichkeit in der Versorgung',
        Beschreibung: 'Implementierung von Maßnahmen zur Sicherstellung einer konstanten und zuverlässigen Energieversorgung, um das Vertrauen der Kunden zu festigen.',
        Produkt_Fit_Markt_Matrix: [
          { Produkt_Fit_Markt_Matrix_Merkmal_Name: 'Sicher / verlässlich', Produkt_Fit_Markt_Matrix_Merkmal_Prozent: 90 },
        ],
      },
      {
        Name: 'Kundenbefragungen und Feedback',
        Beschreibung: 'Regelmäßige Befragungen der Kunden im Vertriebsgebiet zur Erfassung von Bedürfnissen und Wünschen, um das Produkt kontinuierlich zu verbessern und anzupassen.',
        Produkt_Fit_Markt_Matrix: [
          { Produkt_Fit_Markt_Matrix_Merkmal_Name: 'Transparent', Produkt_Fit_Markt_Matrix_Merkmal_Prozent: 100 },
        ],
      },
    ],
    Produkt_Fit_Staerken: [],
    Produkt_Fit_Schwaechen: [],
    Produkt_Fit_Vertriebsfokus: 'Der Vertriebsfokus sollte auf der Betonung von Nachhaltigkeit und regionaler Verbundenheit liegen, um umweltbewusste junge Erwachsene anzusprechen. Innovative und flexible Tarifmodelle, die auf die Bedürfnisse dieser Zielgruppe abgestimmt sind, sollten hervorgehoben werden. Zudem ist eine transparente Kommunikation über die Herkunft des Ökostroms entscheidend, um Vertrauen aufzubauen und eine langfristige Kundenbindung zu fördern. Regionales Engagement kann zusätzlich die Markenwahrnehmung stärken.',
    Produkt_Zusammenfassung: '',
  };

  return {
    main: mockData,
  };
}
